import { type UseQueryResult, useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import { useEnabledQuery } from '../../../../ui/hooks/useEnabledQuery';
import { getMutationSchema } from '../../../../ui/util/getMutationSchema';
import { ResourceType } from '../../../enumerations/ResourceType';
import { GraphDiagramOutlineSchema } from '../../../outlines/graphDiagramOutline';
import { get } from '../../../resource-base/APIVersion1';
import { parseRawData, useOnUnauthorized } from '../../../resource-base/adapters/AdapterUtils';
import { useMultiResourceSingleParentedVersionedResourceAdapterActions } from '../../../resource-base/adapters/useMultiResourceSingleParentedVersionedResourceAdapterActions';
import {
    defaultVersionedArtifactResource,
    VersionedArtifactResourceSchema,
} from '../../../resource-base/artifactResourceTypes';
import { ParamConstants } from '../../../resource-base/types';

export enum ProjectVersionPropertyValidation {
    VERSION_INFO = 'VERSION_INFO',
    FEEDBACK_LINK = 'FEEDBACK_LINK',
}

export const ProjectVersionResourceSchema = VersionedArtifactResourceSchema.extend({
    resourceType: z.literal(ResourceType.PROJECT_VERSION).default(ResourceType.PROJECT_VERSION),
    feedbackLink: z.string().nullable(),
});

export const ProjectVersionMutationResourceSchema = getMutationSchema(ProjectVersionResourceSchema);

export type ProjectVersionResource = z.infer<typeof ProjectVersionResourceSchema>;

export const defaultProjectVersionResource: ProjectVersionResource = {
    ...defaultVersionedArtifactResource,
    feedbackLink: null,
    resourceType: ResourceType.PROJECT_VERSION,
};

export function useProjectVersionActions() {
    const onUnauthorized = useOnUnauthorized();
    const getEnabled = useEnabledQuery();
    const resourceType = ResourceType.PROJECT_VERSION;

    const actions = useMultiResourceSingleParentedVersionedResourceAdapterActions(
        'project-version',
        resourceType,
        ProjectVersionResourceSchema
    );

    const useGetVersionGraph = (parentId: string | undefined | null) => {
        return useQuery({
            queryKey: [parentId, resourceType, 'VERSION_GRAPH'],
            queryFn: () =>
                get(`project-version/graph-diagram?${ParamConstants.PARENT_ID_REQUEST_PARAM_NAME}=${parentId}`).then(
                    (result) => {
                        if (result.status === 200) {
                            return result
                                .json()
                                .then((outline) => parseRawData(outline, GraphDiagramOutlineSchema.array()));
                        } else if (result.status === 401) {
                            return onUnauthorized(result, null).then((_) => {
                                throw Error('Unauthorized!');
                            });
                        }
                    }
                ),
            enabled: getEnabled(parentId),
        });
    };

    const useGetByPublishment = (
        publishmentTitle: string | undefined | null,
        parentId: string | undefined | null
    ): UseQueryResult<ProjectVersionResource | null> => {
        return useQuery({
            queryKey: [parentId, publishmentTitle, resourceType, 'BY_PUBLISHMENT'],
            queryFn: () =>
                get(
                    `project-version/by-publishment?${ParamConstants.PARENT_ID_REQUEST_PARAM_NAME}=${parentId}&publishment=${publishmentTitle}`
                ).then((result) => actions.processGetResourceResponse(result)),
            enabled: getEnabled(parentId && publishmentTitle),
        });
    };

    return {
        ...actions,
        useGetVersionGraph,
        useGetByPublishment,
    };
}
